import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addItemToCollection, deleteItemFromCollection } from '../actions/collections.actions';
import {
	CollectionContentAdded,
	type CollectionContentAddedProperties,
	CollectionContentRemoved,
	type CollectionContentRemovedProperties,
} from '../ampli';
import { selectUser } from '../reducers/user.selectors';
import services from '../services';
import type { ContentItem } from '../types/AppContent';
import { getAmpliContentEventPayload, getSearchIdWithFallback } from './ampli';

const useCollectionActions = (collectionId: string) => {
	const dispatch = useDispatch();
	const user = useSelector(selectUser);

	const addItem = useCallback(
		(item: ContentItem) => {
			dispatch(addItemToCollection(collectionId, item.type, item.id));
			if (user) {
				const eventProperties = getAmpliContentEventPayload(item, item.type, {
					collectionPreexistence: 'existing collection',
				});

				const searchId = getSearchIdWithFallback();
				const payload: CollectionContentAddedProperties = {
					content: eventProperties.content as unknown as string[],
					'collection preexistence': 'existing collection',
					...(searchId && { 'search id': searchId }),
				};

				services.track.ampliEventTrack({
					event: new CollectionContentAdded(payload),
				});
			}
		},
		[dispatch, user, collectionId]
	);

	const removeItem = useCallback(
		(item: ContentItem) => {
			dispatch(deleteItemFromCollection(collectionId, item.type, item.id));
			if (user) {
				const eventProperties = getAmpliContentEventPayload(item, item.type);
				const collectionContentRemovedPayload = {
					content: eventProperties.content,
				};
				services.track.ampliEventTrack({
					event: new CollectionContentRemoved(
						collectionContentRemovedPayload as CollectionContentRemovedProperties
					),
				});
			}
		},
		[dispatch, user, collectionId]
	);

	return { addItem, removeItem };
};

export default useCollectionActions;
